import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';

// Components
import { ItemTileCarousel } from 'dibs-search-product-tile/exports/ItemTile';
import { FavoritesItem } from '../../components/global/FavoriteIcons/FavoritesItem';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
// FINDING-14780 comment out ProductPhotoItemTile until test is ready
// import { ProductPhotoItemTile } from './ProductPhotoItemTile';

// Helpers
import get from 'lodash.get';
import { trackEvent } from 'dibs-tracking';
import { tileMobileGridTypes, SB_MOBILE_ITEM_TILE_DATA_TAG } from '../../constants/sbConstants';

// Styles
import styles from './SharedItemTile.scss';

// Constants
import { SRC_SET_SIZES_TILE } from '../../constants/imageConstants';

export function SharedItemTileComponent(props) {
    const {
        favorites,
        index,
        item,
        isClient,
        isVisuallySimilarEnabled,
        isMobile,
        lazyLoadStartIndex,
        onClick,
        onContentLoaded,
        showInternalSellerName,
        showSellerName,
        showCreatorName,
        showMeasurements,
        showStorefrontOnlyBadge,
        measurementUnit,
        quickViewProps,
        imageLoadVerticalOffset,
        onContentVisible,
        customTileWidthClass,
        imageSize,
        viewer,
        itemSearch,
        gridType,
        tileType,
        lastViewedItemIndex,
    } = props;
    const tileElementRef = useRef(null);
    const { activeQuickView, closeQuickView, openQuickView } = quickViewProps || {};
    const isSbMobileGridToggleDisabled = get(viewer, 'isSbMobileGridToggleDisabled') || false;
    const itemId = get(item, 'serviceId') || '';
    const itemTitle = get(item, 'title') || '';
    // const showPhotosOnly =
    //     !isSbMobileGridToggleDisabled && gridType === tileMobileGridTypes.PRODUCT_PHOTO_ONLY;

    function onQuickViewDetailsClick() {
        trackEvent({
            category: 'ecommerce',
            label: itemId,
            action: 'product detail view - from seo quicklook',
        });
    }

    const favoritesComponent = favorites /* && !showPhotosOnly */ ? (
        <FavoritesItem
            wrapperClass={styles.favoritesWrapper}
            heartIconWrapperClass={styles.heartIconWrapper}
            folderIconWrapperClass={styles.folderIconWrapper}
            itemId={itemId}
            itemTitle={itemTitle}
            fetchFolder={false}
            showTooltipOnFirstClick
            {...favorites}
        />
    ) : null;

    useEffect(() => {
        if (
            !isSbMobileGridToggleDisabled &&
            window.scrollY &&
            index === lastViewedItemIndex &&
            tileElementRef &&
            tileElementRef.current
        ) {
            tileElementRef.current.scrollIntoView({ block: 'center' });
        }
    }, [gridType, index, tileElementRef, lastViewedItemIndex, isSbMobileGridToggleDisabled]);

    return (
        <CurrencyProvider
            render={({ currency, showCurrency }) => {
                const sharedProps = {
                    item,
                    index,
                    imageLoadVerticalOffset,
                    onClick,
                    onContentLoaded,
                    imageSize,
                };
                const photo = item?.productPhotos?.[0];
                if (!photo?.smallPath && !photo?.versions?.[0]?.webPath) {
                    return null;
                }
                return (
                    <div
                        className={classnames(styles.tileWrapper, {
                            [styles.tileWidth]: !customTileWidthClass,
                            [customTileWidthClass]: !!customTileWidthClass,
                            // [styles.photosOnly]: showPhotosOnly,
                            [styles.completeInfo]: true, // !showPhotosOnly,
                        })}
                        data-tn={SB_MOBILE_ITEM_TILE_DATA_TAG}
                        data-tile-index={index}
                        ref={tileElementRef}
                    >
                        {/* {showPhotosOnly ? (
                            <ProductPhotoItemTile
                                {...sharedProps}
                                gridType={gridType}
                                favorites={favorites}
                                lazyLoadStartIndex={lazyLoadStartIndex}
                                lastViewedItemIndex={lastViewedItemIndex}
                            />
                        ) : ( */}
                        <ItemTileCarousel
                            {...sharedProps}
                            useLoFiLazyLoader
                            itemsPerRow={lazyLoadStartIndex}
                            activeQuickView={activeQuickView}
                            isMobile={isMobile}
                            onContentVisible={onContentVisible}
                            openQuickView={openQuickView}
                            closeQuickView={closeQuickView}
                            srcSetSizes={SRC_SET_SIZES_TILE}
                            showQuickViewIcon={!!isClient}
                            showSellerName={showSellerName}
                            showCreatorName={showCreatorName}
                            showInternalSellerName={showInternalSellerName}
                            showStorefrontOnlyBadge={showStorefrontOnlyBadge}
                            currency={currency}
                            showPrice={isClient && showCurrency}
                            measurementUnit={measurementUnit}
                            onQuickViewDetailsClick={onQuickViewDetailsClick}
                            favoritesComponent={favoritesComponent}
                            showMeasurements={showMeasurements}
                            showBrowseSimilar={isVisuallySimilarEnabled && isClient}
                            viewer={viewer}
                            tileType={tileType}
                            itemSearch={itemSearch}
                        />
                        {/* )} */}
                    </div>
                );
            }}
        />
    );
}

SharedItemTileComponent.defaultProps = {
    isMobile: true,
    lazyLoadStartIndex: 2,
    size: 'medium',
    shippingBadge: null,
    gridType: tileMobileGridTypes.COMPLETE_PRODUCT_INFO,
    lastViewedItemIndex: 0,
};

SharedItemTileComponent.propTypes = {
    favorites: PropTypes.object,
    quickViewProps: PropTypes.shape({
        activeQuickView: PropTypes.number,
        closeQuickView: PropTypes.func,
        openQuickView: PropTypes.func,
    }),
    index: PropTypes.number.isRequired,
    isMobile: PropTypes.bool,
    isVisuallySimilarEnabled: PropTypes.bool,
    item: PropTypes.object.isRequired,
    lazyLoadStartIndex: PropTypes.number,
    imageLoadVerticalOffset: PropTypes.number,
    onClick: PropTypes.func,
    onContentLoaded: PropTypes.func,
    showInternalSellerName: PropTypes.bool,
    showSellerName: PropTypes.bool,
    showCreatorName: PropTypes.bool,
    showMeasurements: PropTypes.bool,
    showStorefrontOnlyBadge: PropTypes.bool,
    measurementUnit: PropTypes.string,
    isClient: PropTypes.bool.isRequired,
    onContentVisible: PropTypes.func,
    customTileWidthClass: PropTypes.string,
    imageSize: PropTypes.string,
    hideDivider: PropTypes.bool,
    viewer: PropTypes.object,
    itemSearch: PropTypes.object,
    gridType: PropTypes.oneOf([
        tileMobileGridTypes.COMPLETE_PRODUCT_INFO,
        tileMobileGridTypes.PRODUCT_PHOTO_ONLY,
    ]),
    tileType: PropTypes.string,
    lastViewedItemIndex: PropTypes.number,
};

export const SharedItemTile = createFragmentContainer(SharedItemTileComponent, {
    itemSearch: graphql`
        fragment SharedItemTile_itemSearch on ItemSearchQueryConnection {
            ...ItemTileCarousel_itemSearch
        }
    `,
    viewer: graphql`
        fragment SharedItemTile_viewer on Viewer {
            # isSbMobileGridToggleDisabled: isEnabled(key: "isSbMobileGridToggleDisabled")
            ...ItemTileCarousel_viewer
            @arguments(
                fetchRegionalInfo: $fetchRegionalInfo
                userZipCode: $userZipCode
                userCountryCode: $userCountryCode
            )
        }
    `,
    item: graphql`
        fragment SharedItemTile_item on Item
        @argumentDefinitions(photosLimit: { type: "Int", defaultValue: 1 }) {
            ...ItemTileCarousel_item @arguments(showSeller: $showSeller, isTrade: $isTrade)
            # ...ProductPhotoItemTile_item
            serviceId
            title
            productPhotos: photos(limit: $photosLimit) {
                smallPath
                versions {
                    webPath
                }
            }
        }
    `,
});
